import { FunctionComponent, useRef } from "react";

import { widgetHoverAtom } from "atoms/interaction";
import classnames from "classnames";
import {
  IS_BOX_PLOT_CHART,
  IS_CROSS_PLOT_CHART,
  IS_STACKED_BAR_CHART,
  IS_STACKED_BAR_HORIZONTAL_CHART
} from "constants/chart.constants";
import { EvaChart } from "constants/charts.enums";
import { useChartsContext } from "contexts/ChartContext";
import { useAtomValue } from "jotai";
import styled from "styled-components/macro";

import { useChartEntities } from "hooks/charts";

import { EntityKind } from "models/entityKind";

import { visibilityToggleStyles } from "../../shared/SharedStyles";
import useGlobalNormalizeBy from "../hooks/useGlobalNormalizeBy";
import useOverflowIndex from "../hooks/useOverflowIndex";
import {
  DataSourceToggle,
  DevDebugToggle,
  ForecastToggle,
  FullscreenToggle,
  HistogramToggle,
  JitterPlotToggle,
  LassoBrush,
  LegendToggle,
  LicensedVolumeLineToggle,
  NormalizeByToggle,
  OverflowToggle,
  Popout,
  ProducingToggle,
  RecoveryPVIRadiusToggle,
  ScreenshotToggle,
  SeriesStylesToggle,
  SettingsToggle,
  SlopeLinesToggle,
  SpaghettiToggle,
  StatisticsToggle,
  TimeStepToggle,
  TypewellsToggle,
  ViolinPlotToggle
} from "../toggles";
import AnnotationToggle from "../toggles/AnnotationToggle";
import BestFitToggle from "../toggles/BestFitToggle";
import ProbitDataSourceToggle from "../toggles/ProbitDataSourceToggle";
import ChartFocusToggle from "../toggles/focus/ChartFocusToggle";
import { getNumberOfIconsToDisplay } from "../utils/getNumberOfIconsToDisplay";
import ChartHeader from "./ChartHeader";

const Divider = styled.div`
  width: 1px;
  height: var(--chart-toolbar-height);
  background-color: #d9e1e2;
`;

export const components = [
  <AnnotationToggle key="annotation" />,
  <ChartFocusToggle key="focus" />,
  <DataSourceToggle key="source" />,
  <TimeStepToggle key="timestep" />,
  <NormalizeByToggle key="normalize" />,
  <Divider key="globalSettingsDivider" />,
  <DevDebugToggle key="debugMode" />,
  <SettingsToggle key="settings" />,
  <RecoveryPVIRadiusToggle key="recoveryPVIRadius" />,
  <JitterPlotToggle key="jitterPlotToggle" />,
  <ViolinPlotToggle key="violinPlotToggle" />,
  <ProbitDataSourceToggle key="probitDataSource" />,
  <HistogramToggle key="histogramToggle" />,
  <BestFitToggle key="bestFitToggle" />,
  <LassoBrush key="lasso" />,
  <ProducingToggle key="producing" />,
  <ForecastToggle key="forecast" />,
  <StatisticsToggle key="statistics" />,
  <SlopeLinesToggle key="slopeLines" />,
  <TypewellsToggle key="typewells" />,
  <LicensedVolumeLineToggle key="licensedVolumeLine" />,
  <ScreenshotToggle key="screenshot" />,
  <LegendToggle key="legend" />,
  <SpaghettiToggle key="individual" />,
  <SeriesStylesToggle key="seriesStyles" />,
  <Popout key="popout" />,
  <FullscreenToggle key="fullscreen" />
];

export const midstreamComponents = [
  <ChartFocusToggle key="focus" />,
  <Divider key="globalSettingsDivider" />,
  <DevDebugToggle key="debugMode" />,
  <SettingsToggle key="settings" />,
  <JitterPlotToggle key="jitterPlotToggle" />,
  <ViolinPlotToggle key="violinPlotToggle" />,
  <BestFitToggle key="bestFitToggle" />,
  <LassoBrush key="lasso" />,
  <ForecastToggle key="forecast" />,
  <StatisticsToggle key="statistics" />,
  <LicensedVolumeLineToggle key="licensedVolumeLine" />,
  <ScreenshotToggle key="screenshot" />,
  <LegendToggle key="legend" />,
  <SpaghettiToggle key="individual" />,
  <SeriesStylesToggle key="seriesStyles" />,
  <HistogramToggle key="histogramToggle" />,
  <Popout key="popout" />,
  <FullscreenToggle key="fullscreen" />
];

// TODO chart: optimization: use portal to outportal overflowing components
const ChartToolbar: FunctionComponent = () => {
  const ref = useRef<HTMLDivElement>(null);
  const { id, entityKind } = useChartsContext();

  const { chartType, screenshot, overflowIndex, multiProduct } = useChartEntities(id);
  const isScreenshotActive = screenshot?.active ?? false;
  const isMultiProductActive = multiProduct?.active;
  useGlobalNormalizeBy(isScreenshotActive);
  useOverflowIndex(ref);

  const widgetHoverMap = useAtomValue(widgetHoverAtom);
  const isHover = widgetHoverMap?.[id] ?? false;

  const baseComponents =
    entityKind === EntityKind.Well ? components : midstreamComponents;

  // Filter out specific buttons based on chart type and features
  const visibleComponents = baseComponents.filter(
    (c) =>
      !(
        (c.key === "slopeLines" && chartType !== EvaChart.MaterialBalanceTime) ||
        (c.key === "recoveryPVIRadius" &&
          chartType !== EvaChart.RecoveryPVI &&
          chartType !== EvaChart.RecoveryTime) ||
        (c.key === "annotation" &&
          (chartType !== EvaChart.RateDate || isMultiProductActive)) ||
        (c.key === "jitterPlotToggle" && !IS_BOX_PLOT_CHART(chartType)) ||
        (c.key === "violinPlotToggle" && !IS_BOX_PLOT_CHART(chartType)) ||
        (c.key === "bestFitToggle" && !IS_CROSS_PLOT_CHART(chartType)) ||
        (c.key === "probitDataSource" && chartType !== EvaChart.Probit) ||
        (c.key === "histogramToggle" &&
          !IS_STACKED_BAR_CHART(chartType) &&
          !IS_STACKED_BAR_HORIZONTAL_CHART(chartType))
      )
  );

  // Determine the number of icons to display based on the chart type and overflow index
  const sliceEndIndex = getNumberOfIconsToDisplay(chartType, overflowIndex);

  // Partition the filtered list once so the toolbar and the overflow menu are
  // complementary slices of the SAME array — guaranteeing no button appears in both.
  let toolbarComponents = visibleComponents.slice(0, sliceEndIndex);
  let overflowComponents = visibleComponents
    .slice(sliceEndIndex)
    .filter((c) => c.key !== "globalSettingsDivider");

  // Not worth an overflow menu for fewer than 2 items — show everything instead.
  if (overflowComponents.length < 2) {
    toolbarComponents = visibleComponents;
    overflowComponents = [];
  }

  // Remove the divider if it is the last element of the visible row
  if (toolbarComponents[toolbarComponents.length - 1]?.key === "globalSettingsDivider") {
    toolbarComponents = toolbarComponents.slice(0, toolbarComponents.length - 1);
  }

  const visible = screenshot?.active || isHover;
  const wrapperClassnames = classnames({ visible });

  return (
    <Wrapper ref={ref} className={wrapperClassnames} data-testid="chart-toolbar">
      {!isScreenshotActive && (
        <>
          <ChartHeader />

          {/* visible toolbar icons */}
          <ToolbarContent>
            {/* visible toolbar icons */}
            <GapWrapper>{toolbarComponents}</GapWrapper>

            {/* more-toggle for overflowing toolbar icons */}
            <OverflowToggle overflowComponents={overflowComponents} />
          </ToolbarContent>
        </>
      )}
    </Wrapper>
  );
};

export default ChartToolbar;

const GapWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const Wrapper = styled.div`
  ${visibilityToggleStyles};
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: var(--chart-toolbar-height);
  display: flex;
  align-items: center;
  background-color: var(--white);
  box-shadow: 0 14px 16px 0 rgb(45 53 63 / 10%), inset 0 7px 8px -10px rgb(45 53 63 / 15%);
  z-index: 50;
  padding-right: 4px;
`;

const ToolbarContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  width: 100%;
  padding-right: 4px;
`;
